import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { garageSpecialRequestService } from '../../services/apiService';

const SERVICE_OPTIONS = [
  'ECU Tune',
  'Dyno Session',
  'Exhaust Upgrade',
  'Intake / Turbo / Supercharger',
  'Suspension Setup',
  'Brake Upgrade',
  'Engine Build',
];

export default function CustomTuningJobForm() {
  const [searchParams] = useSearchParams();
  const garageId = searchParams.get('garageId') || '';
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    make: '',
    model: '',
    year: '',
    mileageKm: '',
    usageType: 'daily_driver',
    notes: '',
    requestedServices: [],
  });

  const canSubmit = useMemo(() => form.name.trim() && form.phone.trim(), [form.name, form.phone]);

  const toggleService = (service) => {
    setForm((prev) => ({
      ...prev,
      requestedServices: prev.requestedServices.includes(service)
        ? prev.requestedServices.filter((s) => s !== service)
        : [...prev.requestedServices, service],
    }));
  };

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await garageSpecialRequestService.createQuoteRequest({
        garageId,
        customerDetails: {
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
        },
        vehicleDetails: {
          make: form.make.trim(),
          model: form.model.trim(),
          year: form.year.trim(),
          mileageKm: form.mileageKm ? Number(form.mileageKm) : null,
        },
        requestedServices: form.requestedServices,
        clientGoals: {
          usageType: form.usageType,
          notes: form.notes.trim(),
        },
      });
      toast.success('Quote request submitted successfully.');
      setForm({
        name: '',
        phone: '',
        email: '',
        make: '',
        model: '',
        year: '',
        mileageKm: '',
        usageType: 'daily_driver',
        notes: '',
        requestedServices: [],
      });
    } catch (error) {
      toast.error(error.message || 'Failed to submit quote request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
      <h1 className="text-2xl font-bold text-gray-900">Custom Workshop Quote Request</h1>
      <p className="mt-2 text-sm text-gray-600">
        Share your build goals. Workshop team will review and send a custom estimate.
      </p>
      <form onSubmit={handleSubmit} className="mt-5 space-y-4 rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="Full name *" value={form.name} onChange={(e) => updateField('name', e.target.value)} />
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="Phone *" value={form.phone} onChange={(e) => updateField('phone', e.target.value)} />
          <input className="rounded-lg border px-3 py-2 text-sm sm:col-span-2" placeholder="Email" value={form.email} onChange={(e) => updateField('email', e.target.value)} />
        </div>

        <div className="grid gap-3 sm:grid-cols-4">
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="Make" value={form.make} onChange={(e) => updateField('make', e.target.value)} />
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="Model" value={form.model} onChange={(e) => updateField('model', e.target.value)} />
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="Year" value={form.year} onChange={(e) => updateField('year', e.target.value)} />
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="Mileage (km)" value={form.mileageKm} onChange={(e) => updateField('mileageKm', e.target.value)} />
        </div>

        <div>
          <p className="mb-2 text-sm font-semibold text-gray-800">Requested services</p>
          <div className="flex flex-wrap gap-2">
            {SERVICE_OPTIONS.map((service) => {
              const active = form.requestedServices.includes(service);
              return (
                <button
                  key={service}
                  type="button"
                  onClick={() => toggleService(service)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${active ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-700'}`}
                >
                  {service}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <select className="rounded-lg border px-3 py-2 text-sm" value={form.usageType} onChange={(e) => updateField('usageType', e.target.value)}>
            <option value="daily_driver">Daily driver</option>
            <option value="weekend_fun">Weekend fun</option>
            <option value="track_only">Track only</option>
            <option value="show_car">Show car</option>
            <option value="other">Other</option>
          </select>
          <div />
        </div>

        <textarea
          className="min-h-28 w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="Notes: power target, sound preference, part preference, reliability concerns"
          value={form.notes}
          onChange={(e) => updateField('notes', e.target.value)}
        />

        <button
          type="submit"
          disabled={!canSubmit || submitting}
          className="w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Submitting...' : 'Submit Quote Request'}
        </button>
      </form>
    </div>
  );
}
